import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ArrowRight, 
  Upload, 
  FileText, 
  Presentation, 
  CheckCircle2, 
  Loader2,
  X,
  Image as ImageIcon,
  Sparkles,
  Clock,
  AlertCircle
} from 'lucide-react';

const EvolveCreateSession = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  
  // Step 1: Topic Details
  const [topicName, setTopicName] = useState('');
  const [conceptOverview, setConceptOverview] = useState('');
  const [notes, setNotes] = useState('');
  
  // Step 2: File Upload
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Step 3: Preview
  const [slides, setSlides] = useState([]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (selectedFile) => {
    const allowedTypes = [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/pdf'
    ];
    const allowedExtensions = ['.ppt', '.pptx', '.pptm', '.pdf'];
    
    const fileName = selectedFile.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
      toast.error('Invalid file type', {
        description: 'Please upload a PowerPoint (.pptx, .ppt) or PDF file'
      });
      return;
    }
    
    if (selectedFile.size > 100 * 1024 * 1024) {
      toast.error('File too large', {
        description: 'Maximum file size is 100MB'
      });
      return;
    }
    
    setFile(selectedFile);
  };

  const handleStep1Submit = async () => {
    if (!topicName.trim()) {
      toast.error('Please enter a topic name');
      return;
    }
    if (!conceptOverview.trim()) {
      toast.error('Please enter a concept overview');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/evolve/sessions', {
        topic_name: topicName.trim(),
        concept_overview: conceptOverview.trim(),
        notes: notes.trim() || null
      });
      
      setSessionId(response.data.id);
      setStep(2);
      toast.success('Session created!');
    } catch (error) {
      toast.error('Failed to create session', {
        description: error.response?.data?.detail || 'Please try again'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !sessionId) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload file
      await api.post(`/evolve/sessions/${sessionId}/upload-presentation`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      setUploadProgress(100);
      toast.success('File uploaded! Processing...');
      
      // Start polling for processing status
      pollProcessingStatus();
      
    } catch (error) {
      toast.error('Upload failed', {
        description: error.response?.data?.detail || 'Please try again'
      });
      setUploading(false);
    }
  };

  const pollProcessingStatus = async () => {
    const maxAttempts = 60;
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await api.get(`/evolve/sessions/${sessionId}/processing-status`);
        setProcessingStatus(response.data);
        
        if (response.data.processing_status === 'complete') {
          setUploading(false);
          toast.success(`Processed ${response.data.slide_count} slides!`);
          
          // Fetch slides
          const slidesRes = await api.get(`/evolve/sessions/${sessionId}/slides`);
          setSlides(slidesRes.data.slides || []);
          setStep(3);
          return;
        }
        
        if (response.data.processing_status === 'failed') {
          setUploading(false);
          toast.error('Processing failed', {
            description: response.data.error || 'Please try uploading again'
          });
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setUploading(false);
          toast.error('Processing timeout', {
            description: 'Please try again or use a smaller file'
          });
        }
      } catch (error) {
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        }
      }
    };
    
    poll();
  };

  const handleStartSession = () => {
    navigate(`/evolve/live/${sessionId}`);
  };

  const getProcessingStatusText = () => {
    if (!processingStatus) return 'Preparing...';
    switch (processingStatus.processing_status) {
      case 'processing': return 'Processing file...';
      case 'converting': return 'Converting to slides...';
      case 'extracting': return 'Extracting slides...';
      case 'complete': return 'Complete!';
      default: return 'Processing...';
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#F9F8F6]">
        <div className="p-6 lg:p-8 max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="mb-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/evolve')}
              className="mb-4 -ml-2 text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Create New Session
            </h1>
            <p className="text-slate-600 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Set up your presentation coaching session
            </p>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between max-w-md">
              {[1, 2, 3].map((stepNum) => (
                <div key={stepNum} className="flex items-center">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all
                    ${step >= stepNum 
                      ? 'bg-[#E07A5F] text-white' 
                      : 'bg-slate-200 text-slate-500'
                    }
                  `}>
                    {step > stepNum ? <CheckCircle2 className="h-5 w-5" /> : stepNum}
                  </div>
                  {stepNum < 3 && (
                    <div className={`w-20 h-1 mx-2 rounded ${step > stepNum ? 'bg-[#E07A5F]' : 'bg-slate-200'}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between max-w-md mt-2 text-xs text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span>Topic</span>
              <span>Upload</span>
              <span>Preview</span>
            </div>
          </div>

          {/* Step 1: Topic Details */}
          {step === 1 && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Presentation Details</CardTitle>
                <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Tell us about your presentation topic
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="topicName" className="text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Topic Name *
                  </Label>
                  <Input
                    id="topicName"
                    placeholder="e.g., Q3 Sales Performance Review"
                    value={topicName}
                    onChange={(e) => setTopicName(e.target.value)}
                    className="border-slate-200 focus:ring-2 focus:ring-[#E07A5F]/50 focus:border-[#E07A5F]"
                    data-testid="topic-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="conceptOverview" className="text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Concept Overview *
                  </Label>
                  <Textarea
                    id="conceptOverview"
                    placeholder="Briefly describe what your presentation is about..."
                    value={conceptOverview}
                    onChange={(e) => setConceptOverview(e.target.value)}
                    rows={4}
                    className="border-slate-200 focus:ring-2 focus:ring-[#E07A5F]/50 focus:border-[#E07A5F] resize-none"
                    data-testid="concept-overview-input"
                  />
                  <p className="text-xs text-slate-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    This helps the AI generate relevant Q&A questions
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Additional Notes (Optional)
                  </Label>
                  <Textarea
                    id="notes"
                    placeholder="Any specific areas you want to focus on..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="border-slate-200 focus:ring-2 focus:ring-[#E07A5F]/50 focus:border-[#E07A5F] resize-none"
                    data-testid="notes-input"
                  />
                </div>
                
                <div className="flex justify-end pt-4">
                  <Button 
                    onClick={handleStep1Submit}
                    disabled={loading || !topicName.trim() || !conceptOverview.trim()}
                    className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                    data-testid="continue-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: File Upload */}
          {step === 2 && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Upload Presentation</CardTitle>
                <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Upload your PowerPoint or PDF file
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Drop Zone */}
                <div
                  className={`
                    relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                    ${dragActive 
                      ? 'border-[#E07A5F] bg-[#E07A5F]/5' 
                      : file 
                        ? 'border-[#81B29A] bg-[#81B29A]/5' 
                        : 'border-slate-300 hover:border-slate-400 bg-slate-50'
                    }
                  `}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="file-drop-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".ppt,.pptx,.pptm,.pdf"
                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    className="hidden"
                    data-testid="file-input"
                  />
                  
                  {file ? (
                    <div className="flex flex-col items-center">
                      <div className="p-4 rounded-full bg-[#81B29A]/20 mb-4">
                        <FileText className="h-8 w-8 text-[#81B29A]" />
                      </div>
                      <p className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {file.name}
                      </p>
                      <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); setFile(null); }}
                        className="mt-2 text-slate-500 hover:text-rose-500"
                        data-testid="remove-file-btn"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className="p-4 rounded-full bg-slate-100 mb-4">
                        <Upload className="h-8 w-8 text-slate-400" />
                      </div>
                      <p className="font-semibold text-slate-700 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Drop your file here or click to browse
                      </p>
                      <p className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        PowerPoint (.pptx, .ppt) or PDF • Max 100MB
                      </p>
                    </div>
                  )}
                </div>

                {/* Upload Progress */}
                {uploading && (
                  <div className="space-y-3 p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {getProcessingStatusText()}
                      </span>
                      <Loader2 className="h-4 w-4 animate-spin text-[#E07A5F]" />
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                    {processingStatus?.slide_count > 0 && (
                      <p className="text-xs text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Found {processingStatus.slide_count} slides
                      </p>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between pt-4">
                  <Button 
                    variant="outline"
                    onClick={() => setStep(1)}
                    disabled={uploading}
                    className="border-slate-300"
                    data-testid="back-step-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleUpload}
                    disabled={!file || uploading}
                    className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                    data-testid="upload-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload & Process
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Preview */}
          {step === 3 && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Preview Your Slides</CardTitle>
                <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {slides.length} slides ready • Review before starting your session
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Slides Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {slides.map((slide, index) => (
                    <SlidePreview 
                      key={slide.slide_number}
                      slide={slide}
                      apiUrl={process.env.REACT_APP_BACKEND_URL}
                    />
                  ))}
                </div>

                {/* Session Info */}
                <div className="p-4 rounded-lg bg-[#81B29A]/10 border border-[#81B29A]/20">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-[#81B29A] mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Ready to start your coaching session
                      </p>
                      <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        You will present your slides while being recorded. After presenting, you will answer 3 AI-generated Q&A questions. Finally, you will receive comprehensive feedback on your performance.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-between pt-4">
                  <Button 
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="border-slate-300"
                    data-testid="back-preview-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleStartSession}
                    className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white gap-2"
                    data-testid="start-session-btn"
                  >
                    <Presentation className="h-4 w-4" />
                    Start Presenting
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </MainLayout>
  );
};


// SlidePreview Component - Handles image loading with proper error states
const SlidePreview = ({ slide, apiUrl }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  
  useEffect(() => {
    // Fetch image with credentials to handle httpOnly cookie auth
    const loadImage = async () => {
      try {
        const response = await fetch(`${apiUrl}${slide.url}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to load slide');
        }
        
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
        setImageLoaded(true);
      } catch (error) {
        console.error('Slide load error:', error);
        setImageError(true);
      }
    };
    
    loadImage();
    
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [apiUrl, slide.url]);
  
  return (
    <div className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-video bg-slate-100">
      {imageLoaded && imageSrc && (
        <img 
          src={imageSrc}
          alt={`Slide ${slide.slide_number}`}
          className="w-full h-full object-contain bg-white"
        />
      )}
      
      {!imageLoaded && !imageError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-pulse flex flex-col items-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400 mb-2" />
            <span className="text-xs text-slate-400">Loading...</span>
          </div>
        </div>
      )}
      
      {imageError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50">
          <ImageIcon className="h-8 w-8 text-slate-300 mb-2" />
          <span className="text-sm text-slate-500">Slide {slide.slide_number}</span>
          <span className="text-xs text-slate-400">Preview unavailable</span>
        </div>
      )}
      
      <div className="absolute bottom-2 left-2">
        <Badge variant="secondary" className="bg-black/60 text-white text-xs">
          {slide.slide_number}
        </Badge>
      </div>
    </div>
  );
};

export default EvolveCreateSession;
